/**
 * "Your story so far": the fan's OWN participation (rooms joined, calls made, stories published),
 * from `GET /fans/:id/director-history`. Not a social graph — nothing here is about other fans.
 */
import { directorClock, directorTeamName, TEAMS, type DirectorLanguage } from '@clutch/core';
import { pick } from './i18n';
import type { DirectorHistory, HistoryParticipation } from './api';

const timestamp = (value?: string) => { const n = Date.parse(value ?? ''); return Number.isFinite(n) ? n : 0; };
export const participationTime = (p: HistoryParticipation, memory: DirectorHistory['memory'] = []) => Math.max(timestamp(p.lastActivityAt), timestamp(p.call?.serverLockedAt), ...memory.filter((m) => m.roomId === p.roomId).map((m) => timestamp(m.at)));
/** Stable across room-map/restart ordering. Unknown activity never masquerades as a recent visit. */
export function recentParticipation(participation: readonly HistoryParticipation[], memory: DirectorHistory['memory'] = []): HistoryParticipation[] {
  return [...participation].sort((a, b) => participationTime(b, memory) - participationTime(a, memory) || a.roomId.localeCompare(b.roomId));
}

export type HistoryKind = 'called' | 'missed' | 'pending' | 'void' | 'watched';
export type HistoryCard = {
  roomId: string;
  gameId: string;
  away: string;
  home: string;
  kind: HistoryKind;
  title: string;
  detail: string;
  /** The one action on the card. */
  action: { kind: 'story' | 'rematch' | 'continue'; label: string };
  /** Already watched this catch-up through the room's current cursor. */
  seen: boolean;
  storyPath?: string;
};

export function historyCards(h: DirectorHistory | undefined, lang: DirectorLanguage, limit = 4): HistoryCard[] {
  if (!h) return [];
  const c = pick({
    en: { called: (t: string) => `You called the ${t}`, backed: (t: string) => `You backed the ${t}`, story: 'Your story', relive: 'Relive it', rematch: 'Rematch', void: 'Call voided', catchUp: 'Catch up', pending: 'Your call is still alive', locked: 'locked', continue: 'Continue', watched: (a: string, h: string) => `You were in ${a} at ${h}`, noCall: 'No call made' },
    hi: { called: (t: string) => `आपने ${t} पर सही कॉल की`, backed: (t: string) => `आपने ${t} को चुना था`, story: 'अपनी कहानी देखें', relive: 'कहानी बनाइए', rematch: 'फिर चुनौती दें', void: 'कॉल रद्द हुई', catchUp: 'कैच अप', pending: 'आपकी कॉल अभी ज़िंदा है', locked: 'लॉक', continue: 'जारी रखें', watched: (a: string, h: string) => `आप ${a} बनाम ${h} में थे`, noCall: 'कोई कॉल नहीं' },
    zh: { called: (t: string) => `你预测了${t}获胜，猜对了`, backed: (t: string) => `你支持了${t}`, story: '你的故事', relive: '重温这一刻', rematch: '再来一场', void: '预测已作废', catchUp: '了解赛况', pending: '你的预测尚未揭晓', locked: '已锁定', continue: '继续', watched: (a: string, h: string) => `你参与了${a}对阵${h}`, noCall: '尚未预测' },
  }, lang);
  const out: HistoryCard[] = [];
  const seenGames = new Set<string>();
  for (const p of recentParticipation(h.participation, h.memory)) {
    if (seenGames.has(p.gameId)) continue; // one card per game: the latest room
    seenGames.add(p.gameId);
    const mem = h.memory.filter((m) => m.roomId === p.roomId).sort((a, b) => timestamp(b.at) - timestamp(a.at))[0];
    const seen = !!mem && mem.seenThroughSeq >= p.asOfSeq;
    const story = h.stories.filter((s) => s.game.gameId === p.gameId).sort((a, b) => timestamp(b.publishedAt) - timestamp(a.publishedAt) || a.storyId.localeCompare(b.storyId))[0];
    const r = p.receipt;
    const matchup = `${p.away} @ ${p.home}`;
    const base = { roomId: p.roomId, gameId: p.gameId, away: p.away, home: p.home, seen, ...(story ? { storyPath: story.sharePath } : {}) };
    if (r) {
      const choice = Object.values(TEAMS).find((t) => t.name === r.choiceLabel || t.tricode === r.choiceLabel);
      const label = choice ? directorTeamName(choice.tricode, lang) : r.choiceLabel;
      const stamp = `${directorClock(r.lockedAt.period, r.lockedAt.clockSec, lang)} · ${Math.round(r.pChoiceAtLock * 100)}%`;
      if (r.result === 'correct')
        out.push({ ...base, kind: 'called', title: c.called(label), detail: `${stamp} · ${r.finalScore}`,
          action: { kind: 'story', label: story ? c.story : c.relive } });
      else if (r.result === 'incorrect')
        out.push({ ...base, kind: 'missed', title: c.backed(label), detail: `${stamp} · ${r.finalScore}`,
          action: { kind: 'rematch', label: c.rematch } });
      else out.push({ ...base, kind: 'void', title: c.void, detail: r.finalScore, action: { kind: 'continue', label: c.catchUp } });
      continue;
    }
    if (p.call && p.call.status === 'confirmed') {
      out.push({ ...base, kind: 'pending', title: c.pending,
        detail: `${matchup} · ${c.locked} ${directorClock(p.call.lockedAt.period, p.call.lockedAt.clockSec, lang)} · ${Math.round(p.call.pChoiceAtLock * 100)}%`,
        action: { kind: 'continue', label: c.continue } });
      continue;
    }
    out.push({ ...base, kind: 'watched', title: c.watched(directorTeamName(p.away, lang), directorTeamName(p.home, lang)),
      detail: c.noCall, action: { kind: 'continue', label: c.catchUp } });
  }
  return out.slice(0, limit);
}
